import DraggableModal from 'components/organisms/draggableModal';
import { usePatientDevices } from 'infrastructure/hooks/device/use-patient-devices';
import { useRpmPatientActivity } from 'infrastructure/hooks/rpm';
import { useAppDispatch, useAppSelector } from 'store';
import useUser from 'utils/useUser';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import dayjs from 'dayjs';
import { Divider } from 'antd';
import BaseSelectBox from 'components/atoms/base-select-box';
import SearchIcon from 'components/atoms/icons/search.svg?react';

import s from './styles.module.scss';
import ReadingItem from './reading-item';

import type {
  IActivityData,
  TReadingGroup,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

const DEFAULT_RANGE: [Date, Date] = [
  dayjs().startOf('M').toDate(),
  dayjs().endOf('M').toDate(),
];

type TList = { label: string; value: TReadingGroup }[];

const READING_GROUP_LIST: TList = [
  { label: 'Unactioned', value: 'unreviewed' },
  { label: 'Actioned', value: 'reviewed' },
  { label: 'Erroneous', value: 'erroneous' },
];

const readingSortMap: Record<TReadingGroup, number> = {
  unreviewed: 1,
  reviewed: 2,
  erroneous: 3,
};

const sortFn = (a: IActivityData, b: IActivityData) => {
  if (!a.groupType || !b.groupType) return 0;
  return readingSortMap[a.groupType] - readingSortMap[b.groupType];
};

interface LinkedCallReadingsModalProps {
  patientGuid: string;
  clinicGuid: string;
  isOpenConnect?: boolean;
}

const LinkedCallReadingsModal: FC<LinkedCallReadingsModalProps> = ({
  patientGuid,
  clinicGuid,
  isOpenConnect = true,
}) => {
  const [selectedReadings, setSelectedReadings] = useState<IActivityData[]>([]);
  const { isMedicalAssistantRole } = useUser();
  const [startTime, endTime] = useAppSelector<[number, number]>(
    (state) => state.patient.dates,
  );
  const { loading: isLoadingPatientDevices, devices } = usePatientDevices({
    patientGuid,
  });
  const deviceIds = devices?.map((device) => device.deviceId).join(',') ?? '';
  const { readingGroups } = useAppSelector(
    (state) => state.linkedCallsReadings,
  );

  const { activity, isLoading, isFetching, refetch, isRefetching } =
    useRpmPatientActivity({
      clinicGuid,
      deviceIds,
      patientGuid,
      reviewed: false,
      onlyIsErroneous: false,
      reverse: false,
      allowCriticals: isMedicalAssistantRole ? true : undefined,
      startTime: startTime ?? DEFAULT_RANGE[0].getTime(),
      endTime: endTime ?? DEFAULT_RANGE[1].getTime(),
      meta: { page: 1, items: 500 },
      readingGroups: readingGroups.length ? readingGroups : [],
    });
  const { t } = useTranslation();
  const { isPopupOpen } = useAppSelector((state) => state.linkedCallsReadings);
  const isOpenConnectPopup = useAppSelector(
    (state) => state.connect.connectPopoutOpen,
  );
  const prevIsOpenConnectPopup = useRef(isOpenConnectPopup);
  const {
    linkedCallsReadings: { setIsPopupOpen, setReadingGroups, setReadingIds },
    connect: { handleConnectPopoutOpen },
  } = useAppDispatch();

  const title = t('linkedCallReadings.linkReadingsToCallCount', {
    count: selectedReadings.length,
  });
  const isEmptyData =
    activity?.data?.length === 0 && selectedReadings.length === 0;
  const isAllSelected =
    (!isEmptyData && selectedReadings.length === activity?.data?.length) ||
    (selectedReadings.length > 0 && activity?.data?.length === 0);

  const handleClose = () => {
    setSelectedReadings([]);
  };

  const handleLinkReadings = () => {
    if (isOpenConnect) handleConnectPopoutOpen(true);
    const selectedReadingsIds = selectedReadings.map((item) => ({
      readingId: item.id,
      deviceId: item.deviceId,
    }));
    setReadingIds(selectedReadingsIds);
    setIsPopupOpen(false);
  };

  const handleChangeFilter = (newGroups: TReadingGroup[]) => {
    setReadingGroups(newGroups);
  };

  const selectAll = () => {
    if (isAllSelected) setSelectedReadings([]);
    else if (activity?.data?.length) setSelectedReadings(activity.data);
  };

  const rightTitle = (
    <div className={s.rightTitle}>
      <div>
        <p>{t('linkedCallReadings.readingGroups')}:</p>
        <div>
          <BaseSelectBox
            minWidth="200px"
            options={READING_GROUP_LIST}
            mode="multiple"
            value={READING_GROUP_LIST.filter((el) =>
              readingGroups.includes(el.value),
            )}
            onChange={handleChangeFilter}
            maxTagCount="responsive"
          />
        </div>
      </div>
      <BaseButton
        type="secondary"
        label={
          isAllSelected ? t('controls.deselectAll') : t('controls.selectAll')
        }
        onClick={selectAll}
        width={100}
        dataCy="select-deselect-all-readings-bnt"
        disabled={isEmptyData}
      />
      <BaseButton
        type="secondary"
        label={t('controls.refresh')}
        onClick={() => refetch()}
        width={80}
        dataCy="refresh-readings-query-bnt"
      />
    </div>
  );

  const modalFooter = (
    <div className={s.footer}>
      <BaseButton
        type="secondary"
        label={t('controls.cancel')}
        onClick={() => setIsPopupOpen(false)}
        width={80}
        dataCy="link-readings-to-call-modal_cancel-btn"
      />
      <BaseButton
        label={isOpenConnect ? t('controls.linkAndCall') : t('controls.link')}
        onClick={handleLinkReadings}
        width={130}
        disabled={selectedReadings.length === 0}
        dataCy="link-readings-to-call-modal_submit-btn"
      />
    </div>
  );

  const loading =
    isLoadingPatientDevices || isLoading || isFetching || isRefetching;

  const filteredReadings = useMemo(() => {
    return (
      activity?.data
        ?.toSorted(sortFn)
        .filter((item) => !selectedReadings.some((el) => el.id === item.id)) ??
      []
    );
  }, [activity?.data, selectedReadings]);

  useEffect(() => {
    // checks is connect modal was closed
    if (prevIsOpenConnectPopup.current && !isOpenConnectPopup) {
      handleClose();
    }

    prevIsOpenConnectPopup.current = isOpenConnectPopup;
  }, [isOpenConnectPopup]);

  return (
    <DraggableModal
      isOpen={isPopupOpen}
      toggle={() => setIsPopupOpen(false)}
      title={title}
      rightTitle={rightTitle}
      footer={modalFooter}
      modalClassName={s.modal}
      dataCy="link-readings-to-call-modal"
    >
      {loading ? (
        <BaseLoader loading className={s.loader} />
      ) : (
        <div className={s['modal-body']}>
          {selectedReadings.length > 0 && (
            <p className={s['selected-readings']}>
              {t('linkedCallReadings.selectedReadings')}
            </p>
          )}
          {selectedReadings.map((item, idx) => {
            return (
              <ReadingItem
                key={item.id + item.dateTime + idx}
                createdAt={item.createdAt}
                idx={idx}
                timezone={item.patient.timezone}
                globalStatus={item.globalStatus}
                values={item.value}
                isChecked
                statuses={item.statuses}
                readingType={item.readingType}
                thresholdName={item.thresholdName}
                isRecovered={item.isRecovered}
                thresholds={item.patient.threshold}
                groupType={item?.groupType}
                onChange={() => {
                  setSelectedReadings((prev) =>
                    prev.filter((el) => el.id !== item.id),
                  );
                }}
              />
            );
          })}
          {selectedReadings.length > 0 && <Divider />}
          {filteredReadings.map((item, idx) => {
            return (
              <ReadingItem
                key={item.id + idx}
                createdAt={item.createdAt}
                idx={idx}
                timezone={item.patient.timezone}
                globalStatus={item.globalStatus}
                values={item.value}
                isChecked={false}
                statuses={item.statuses}
                readingType={item.readingType}
                thresholdName={item.thresholdName}
                isRecovered={item.isRecovered}
                thresholds={item.patient.threshold}
                groupType={item?.groupType}
                onChange={() => {
                  setSelectedReadings((prev) => [...prev, item]);
                }}
              />
            );
          })}
          {filteredReadings.length === 0 && selectedReadings.length === 0 && (
            <div className={s['no-readings']}>
              <SearchIcon width={100} height={100} />
              <p>{t('linkedCallReadings.noReadings')}</p>
            </div>
          )}
        </div>
      )}
    </DraggableModal>
  );
};
export default LinkedCallReadingsModal;
